import type {
  MutationOptions,
  QueryOptions,
  useQuery,
} from "@tanstack/react-query";
import { mapValues } from "utils/collection";

export function createQueries<
  TQueryFn extends (...args: any[]) => QueryOptions,
>(key: string, queries?: Record<string, TQueryFn>) {
  if (!queries) return queries;
  return mapValues(queries, (q) => (...args: Parameters<TQueryFn>) => {
    const options = q(...args);
    return {
      ...options,
      queryKey: [key, ...(options.queryKey || [])],
    } as ReturnType<TQueryFn>;
  });
}

/**
 * Same queryKey prefixing as `createQueries`, but without the `QueryOptions`
 * constraint — infinite queries also carry `initialPageParam` and
 * `getNextPageParam`, which that type doesn't cover.
 */
export function createInfiniteQueries<
  TQueryFn extends (...args: any[]) => { queryKey?: unknown[] },
>(key: string, queries?: Record<string, TQueryFn>) {
  if (!queries) return queries;
  return mapValues(queries, (q) => (...args: Parameters<TQueryFn>) => {
    const options = q(...args);
    return {
      ...options,
      queryKey: [key, ...(options.queryKey || [])],
    } as ReturnType<TQueryFn>;
  });
}

export function createModel<
  TQueries extends Record<
    string,
    (
      ...args: any[]
    ) => QueryOptions & Pick<Parameters<typeof useQuery>[0], "throwOnError">
  >,
  TMutations extends Record<
    string,
    (...args: any[]) => MutationOptions<any, any, any, any>
  >,
  TInfiniteQueries extends Record<
    string,
    (...args: any[]) => { queryKey?: unknown[] }
  >,
>(
  key: string,
  queriesAndMutations: {
    queries?: TQueries;
    mutations?: TMutations;
    infiniteQueries?: TInfiniteQueries;
  },
) {
  return {
    queries: createQueries(key, queriesAndMutations.queries),
    mutations: queriesAndMutations.mutations,
    infiniteQueries: createInfiniteQueries(
      key,
      queriesAndMutations.infiniteQueries,
    ),
  } as unknown as {
    queries: TQueries;
    mutations: TMutations;
    infiniteQueries: TInfiniteQueries;
  };
}
